/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  siAirtable,
  siCalendly,
  siDropbox,
  siFacebook,
  siGmail,
  siGoogleads,
  siGoogleanalytics,
  siGooglebigquery,
  siGooglecalendar,
  siGoogledocs,
  siGoogledrive,
  siGooglemaps,
  siGooglemeet,
  siGooglesheets,
  siHubspot,
  siInstagram,
  siLinear,
  siMeta,
  siNotion,
  siShopify,
  siStripe,
  siSupabase,
  siTiktok,
  siWhatsapp,
  siWoocommerce,
  siX,
  siYoutube,
  siZoom,
} from "simple-icons"

export type BrandIcon = {
  id: string
  title: string
  /** Matching tokens — lowercase, alphanumeric. */
  tokens: string[]
  /** SVG path `d` attribute (24×24 viewBox). */
  path: string
  /** Brand color, e.g. "#95BF47". */
  hex: string
}

const withHash = (hex: string) => `#${hex}`

/**
 * Curated brand registry. Each plugin's id/title/capabilities are matched
 * against `tokens` — the first brand whose tokens overlap wins.
 *
 * Order matters: more-specific brands come BEFORE more-general ones
 * (e.g. "googleads" before "facebook"; "woocommerce" before generic "store").
 */
export const BRAND_ICONS: BrandIcon[] = [
  {
    id: "shopify",
    title: siShopify.title,
    tokens: ["shopify"],
    path: siShopify.path,
    hex: withHash(siShopify.hex),
  },
  {
    id: "woocommerce",
    title: siWoocommerce.title,
    tokens: ["woocommerce", "woo"],
    path: siWoocommerce.path,
    hex: withHash(siWoocommerce.hex),
  },
  {
    id: "stripe",
    title: siStripe.title,
    tokens: ["stripe"],
    path: siStripe.path,
    hex: withHash(siStripe.hex),
  },
  {
    id: "hubspot",
    title: siHubspot.title,
    tokens: ["hubspot"],
    path: siHubspot.path,
    hex: withHash(siHubspot.hex),
  },
  {
    id: "meta",
    title: siMeta.title,
    tokens: ["meta", "meta-ads", "metaads"],
    path: siMeta.path,
    hex: withHash(siMeta.hex),
  },
  {
    id: "facebook",
    title: siFacebook.title,
    tokens: ["facebook", "fb"],
    path: siFacebook.path,
    hex: withHash(siFacebook.hex),
  },
  {
    id: "instagram",
    title: siInstagram.title,
    tokens: ["instagram", "ig"],
    path: siInstagram.path,
    hex: withHash(siInstagram.hex),
  },
  {
    id: "tiktok",
    title: siTiktok.title,
    tokens: ["tiktok"],
    path: siTiktok.path,
    hex: withHash(siTiktok.hex),
  },
  {
    id: "x",
    title: siX.title,
    tokens: ["twitter", "x-com"],
    path: siX.path,
    hex: withHash(siX.hex),
  },
  {
    id: "youtube",
    title: siYoutube.title,
    tokens: ["youtube", "yt"],
    path: siYoutube.path,
    hex: withHash(siYoutube.hex),
  },
  {
    id: "googleads",
    title: siGoogleads.title,
    tokens: ["googleads", "gads", "google-ads"],
    path: siGoogleads.path,
    hex: withHash(siGoogleads.hex),
  },
  {
    id: "googleanalytics",
    title: siGoogleanalytics.title,
    tokens: ["googleanalytics", "ga", "google-analytics", "ga4"],
    path: siGoogleanalytics.path,
    hex: withHash(siGoogleanalytics.hex),
  },
  {
    id: "googlebigquery",
    title: siGooglebigquery.title,
    tokens: ["bigquery", "googlebigquery"],
    path: siGooglebigquery.path,
    hex: withHash(siGooglebigquery.hex),
  },
  {
    id: "googledrive",
    title: siGoogledrive.title,
    tokens: ["googledrive", "drive", "gdrive"],
    path: siGoogledrive.path,
    hex: withHash(siGoogledrive.hex),
  },
  {
    id: "googledocs",
    title: siGoogledocs.title,
    tokens: ["googledocs", "gdocs"],
    path: siGoogledocs.path,
    hex: withHash(siGoogledocs.hex),
  },
  {
    id: "googlesheets",
    title: siGooglesheets.title,
    tokens: ["googlesheets", "gsheets", "sheets"],
    path: siGooglesheets.path,
    hex: withHash(siGooglesheets.hex),
  },
  {
    id: "gmail",
    title: siGmail.title,
    tokens: ["gmail"],
    path: siGmail.path,
    hex: withHash(siGmail.hex),
  },
  {
    id: "googlemeet",
    title: siGooglemeet.title,
    tokens: ["googlemeet", "gmeet"],
    path: siGooglemeet.path,
    hex: withHash(siGooglemeet.hex),
  },
  {
    id: "googlecalendar",
    title: siGooglecalendar.title,
    tokens: ["googlecalendar", "gcal", "calendar"],
    path: siGooglecalendar.path,
    hex: withHash(siGooglecalendar.hex),
  },
  {
    id: "googlemaps",
    title: siGooglemaps.title,
    tokens: ["googlemaps", "gmaps", "maps"],
    path: siGooglemaps.path,
    hex: withHash(siGooglemaps.hex),
  },
  {
    id: "notion",
    title: siNotion.title,
    tokens: ["notion"],
    path: siNotion.path,
    hex: withHash(siNotion.hex),
  },
  {
    id: "airtable",
    title: siAirtable.title,
    tokens: ["airtable"],
    path: siAirtable.path,
    hex: withHash(siAirtable.hex),
  },
  {
    id: "linear",
    title: siLinear.title,
    tokens: ["linear"],
    path: siLinear.path,
    hex: withHash(siLinear.hex),
  },
  {
    id: "calendly",
    title: siCalendly.title,
    tokens: ["calendly"],
    path: siCalendly.path,
    hex: withHash(siCalendly.hex),
  },
  {
    id: "supabase",
    title: siSupabase.title,
    tokens: ["supabase"],
    path: siSupabase.path,
    hex: withHash(siSupabase.hex),
  },
  {
    id: "whatsapp",
    title: siWhatsapp.title,
    tokens: ["whatsapp"],
    path: siWhatsapp.path,
    hex: withHash(siWhatsapp.hex),
  },
  {
    id: "dropbox",
    title: siDropbox.title,
    tokens: ["dropbox"],
    path: siDropbox.path,
    hex: withHash(siDropbox.hex),
  },
  {
    id: "zoom",
    title: siZoom.title,
    tokens: ["zoom"],
    path: siZoom.path,
    hex: withHash(siZoom.hex),
  },
]

function normalize(value?: string): string {
  return (value ?? "").trim().toLowerCase().replace(/[^a-z0-9]+/g, " ").trim()
}

/**
 * Search the brand registry for a match against a plugin's tokens.
 * Returns the FIRST brand whose tokens overlap. Brand order in
 * `BRAND_ICONS` controls priority (specific before general).
 */
export function findBrand(input: {
  id?: string
  icon?: string
  title?: string
  capabilities?: string[]
}): BrandIcon | undefined {
  const haystack = [input.id, input.icon, input.title, ...(input.capabilities ?? [])]
    .map(normalize)
    .filter(Boolean)
    .join(" ")
  if (!haystack) return undefined
  const padded = ` ${haystack} `
  return BRAND_ICONS.find((brand) =>
    brand.tokens.some((token) => {
      const normalized = normalize(token)
      if (!normalized) return false
      // Whole-word match to avoid "x" matching "xfile" etc.
      return padded.includes(` ${normalized} `)
    }),
  )
}
